import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Table, Button, Dropdown, Checkbox, Space } from 'antd';
import type { TableProps, ColumnType } from 'antd/es/table';
import { SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import CardList, { CardLayout } from './responsive/CardList';
import { useCompactLayout } from '../hooks/useCompactLayout';
import 'react-resizable/css/styles.css';

/**
 * ConfigurableTable — antd <Table> wrapper adding:
 *   1. Column resize (drag header cell right edge)
 *   2. Column show/hide (gear button → checklist dropdown)
 *   3. Table height resize (drag bottom edge)
 *   4. Reset-to-defaults
 *   5. Per-user per-table persistence in localStorage
 *
 * Usage: drop-in replacement for <Table> with a required `tableId` prop.
 *   <ConfigurableTable tableId="po-list" columns={columns} dataSource={...} ... />
 *
 * Any column can opt out of resize or hide with `resizable: false` / `hideable: false`.
 * Columns without a `key` fall back to `dataIndex` then index for persistence.
 */

export interface ConfigurableColumn<T = any> extends ColumnType<T> {
  resizable?: boolean; // default true
  hideable?: boolean;  // default true
}

export interface ConfigurableTableProps<T = any> extends Omit<TableProps<T>, 'columns'> {
  tableId: string;
  columns: ConfigurableColumn<T>[];
  /** Default table body height in px (vertical scroll). null = no scroll. */
  defaultHeight?: number | null;
  /** Min allowed column width in px (default 60). */
  minColumnWidth?: number;
  /** Min allowed table body height in px (default 120). */
  minTableHeight?: number;
  /** Phone / small-tablet card layout (2026-10-19): which columns head and foot each card. */
  card?: CardLayout;
}

interface StoredPrefs {
  widths: Record<string, number>;
  hidden: string[];
  height: number | null;
}

const STORAGE_PREFIX = 'lasheen-tbl-v1:';

function loadPrefs(tableId: string): StoredPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tableId);
    if (!raw) return { widths: {}, hidden: [], height: null };
    const parsed = JSON.parse(raw);
    return {
      widths: parsed.widths ?? {},
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
      height: typeof parsed.height === 'number' ? parsed.height : null,
    };
  } catch {
    return { widths: {}, hidden: [], height: null };
  }
}

function savePrefs(tableId: string, prefs: StoredPrefs) {
  try {
    localStorage.setItem(STORAGE_PREFIX + tableId, JSON.stringify(prefs));
  } catch {
    // quota or private mode — silently ignore
  }
}

function clearPrefs(tableId: string) {
  try {
    localStorage.removeItem(STORAGE_PREFIX + tableId);
  } catch {
    // ignore
  }
}

/** Stable id for a column used as persistence key. */
function colKeyOf(col: ConfigurableColumn, idx: number): string {
  if (col.key != null) return String(col.key);
  if ((col as any).dataIndex != null) return String((col as any).dataIndex);
  return `col-${idx}`;
}

/** Resizable header cell wired to react-resizable. */
const ResizableTitle: React.FC<any> = (props) => {
  const { onResize, width, resizableEnabled, ...restProps } = props;
  if (!resizableEnabled || width == null) {
    return <th {...restProps} />;
  }
  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="lasheen-col-resize-handle"
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

function ConfigurableTable<T extends object = any>(props: ConfigurableTableProps<T>) {
  const {
    tableId,
    columns: userColumns,
    defaultHeight = null,
    minColumnWidth = 60,
    minTableHeight = 120,
    scroll: userScroll,
    card,
    ...restTable
  } = props;
  const compact = useCompactLayout();

  // Initial prefs from localStorage
  const initialPrefs = useMemo(() => loadPrefs(tableId), [tableId]);

  const [widths, setWidths] = useState<Record<string, number>>(initialPrefs.widths);
  const [hidden, setHidden] = useState<string[]>(initialPrefs.hidden);
  const [height, setHeight] = useState<number | null>(
    initialPrefs.height ?? defaultHeight,
  );

  // Persist on every change
  useEffect(() => {
    savePrefs(tableId, { widths, hidden, height });
  }, [tableId, widths, hidden, height]);

  // Column show/hide menu items
  const menuItems = useMemo(() => {
    return userColumns.map((col, idx) => {
      const key = colKeyOf(col, idx);
      const hideable = col.hideable !== false;
      const label = typeof col.title === 'string' ? col.title : key;
      return { key, label: String(label), hideable };
    });
  }, [userColumns]);

  const toggleHidden = useCallback(
    (key: string, checked: boolean) => {
      setHidden((prev) => {
        if (checked) return prev.filter((k) => k !== key);
        if (prev.includes(key)) return prev;
        return [...prev, key];
      });
    },
    [],
  );

  const resetPrefs = useCallback(() => {
    clearPrefs(tableId);
    setWidths({});
    setHidden([]);
    setHeight(defaultHeight);
  }, [tableId, defaultHeight]);

  // Build the final columns array: filter hidden + attach onHeaderCell for resize
  const processedColumns = useMemo(() => {
    return userColumns
      .map((col, idx) => {
        const key = colKeyOf(col, idx);
        if (hidden.includes(key) && col.hideable !== false) return null;

        const resizableEnabled = col.resizable !== false;
        const currentWidth = widths[key] ?? (col.width as number | undefined);

        const processed: ConfigurableColumn<T> = {
          ...col,
          width: currentWidth,
        };

        if (resizableEnabled && typeof currentWidth === 'number') {
          processed.onHeaderCell = (column: any) => ({
            width: (typeof column?.width === 'number' ? column.width : currentWidth),
            resizableEnabled: true,
            onResize: (_e: React.SyntheticEvent, data: ResizeCallbackData) => {
              const newWidth = Math.max(minColumnWidth, Math.round(data.size.width));
              setWidths((prev) => {
                if (prev[key] === newWidth) return prev;
                return { ...prev, [key]: newWidth };
              });
            },
          } as any);
        }

        return processed;
      })
      .filter(Boolean) as ConfigurableColumn<T>[];
  }, [userColumns, widths, hidden, minColumnWidth]);

  // Table vertical height resize (bottom-edge drag handle)
  const dragStateRef = useRef<{ startY: number; startHeight: number } | null>(null);

  const handleHeightDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startHeight = height ?? 400;
      dragStateRef.current = { startY: e.clientY, startHeight };

      const onMove = (ev: MouseEvent) => {
        if (!dragStateRef.current) return;
        const delta = ev.clientY - dragStateRef.current.startY;
        const next = Math.max(
          minTableHeight,
          dragStateRef.current.startHeight + delta,
        );
        setHeight(next);
      };
      const onUp = () => {
        dragStateRef.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [height, minTableHeight],
  );

  // Compose the scroll prop: auto-grow x to sum of visible column widths so
  // resizing one column doesn't reflow the others. Preserve user scroll.y too.
  const finalScroll = useMemo(() => {
    const base: any = { ...(userScroll || {}) };
    // Sum widths of visible, width-specified columns; add 20% safety margin for
    // action buttons / unsized columns
    const totalColWidth = processedColumns.reduce((sum, c) => {
      const w = typeof c.width === 'number' ? c.width : 0;
      return sum + w;
    }, 0);
    const userX = typeof base.x === 'number' ? base.x : 0;
    const computedX = Math.max(userX, totalColWidth);
    if (computedX > 0) base.x = computedX;
    if (height != null) base.y = height;
    return base;
  }, [userScroll, height, processedColumns]);

  // Custom components: resizable header cell
  const components = useMemo(
    () => ({
      header: {
        cell: ResizableTitle,
      },
    }),
    [],
  );

  // Phones and small tablets: the same rows as cards, built from the visible columns (2026-10-19).
  if (compact) return <CardList<T> {...(restTable as any)} columns={processedColumns as any[]} layout={card} />;

  return (
    <div className="lasheen-configurable-table-wrap">
      <Space style={{ marginBottom: 8, justifyContent: 'flex-end', width: '100%' }}>
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          dropdownRender={() => (
            <div
              style={{
                background: '#fff',
                padding: 12,
                borderRadius: 6,
                boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                minWidth: 220,
                maxHeight: 420,
                overflowY: 'auto',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                Show columns
              </div>
              <Space direction="vertical" style={{ width: '100%' }}>
                {menuItems.map((item) => (
                  <Checkbox
                    key={item.key}
                    disabled={!item.hideable}
                    checked={!hidden.includes(item.key)}
                    onChange={(e) => toggleHidden(item.key, e.target.checked)}
                  >
                    {item.label}
                  </Checkbox>
                ))}
              </Space>
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 8,
                  borderTop: '1px solid #f0f0f0',
                }}
              >
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={resetPrefs}
                  block
                >
                  Reset to defaults
                </Button>
              </div>
            </div>
          )}
        >
          <Button
            size="small"
            icon={<SettingOutlined />}
            title="Columns & table settings"
          >
            Columns
          </Button>
        </Dropdown>
      </Space>

      <Table<T>
        {...restTable}
        columns={processedColumns as any}
        components={components}
        scroll={finalScroll}
        tableLayout="fixed"
      />

      <div
        className="lasheen-tbl-height-handle"
        onMouseDown={handleHeightDragStart}
        title="Drag to resize table height"
      />
    </div>
  );
}

export default ConfigurableTable;
